"use client";

// NOTE: This version keeps your existing Flowbite booking form 100% intact
// and wraps it with the redesigned layout (hero, SAS desk, map + offices directory,
// offices grid, FAQs, and footer CTAs). No changes to your form logic, axios call,
// or EmailService — just dropped in as-is.

import React, { useMemo, useRef, useState } from "react";
import {
  Button as FlowbiteButton,
  Footer,
  Label,
  TextInput,
  Select,
  Checkbox,
} from "flowbite-react";
import axios from "axios";
import {
  HiOutlineLocationMarker,
  HiOutlinePhone,
  HiMail,
  HiChevronLeft,
  HiChevronRight,
} from "react-icons/hi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { sendEmail } from "./EmailService";

export default function Contact() {
  // ---------------- Your original form state & handlers (UNCHANGED) ----------------
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    country: "",
    phonenumber: "",
    serviceType: "",
    agreeTerms: false,
    contactPreference: false,
    receiveUpdates: false,
  });
  const [successMessage, setSuccessMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axios.post(
        "https://unithink-backend.vercel.app/api/consultation/",
        formData
      );

      await sendEmail(formData);

      setLoading(false);
      setSuccessMessage("Form submitted successfully!");
      toast.success("Form submitted successfully!");
      setFormData({
        name: "",
        email: "",
        country: "",
        phonenumber: "",
        serviceType: "",
        agreeTerms: false,
        contactPreference: false,
        receiveUpdates: false,
      });
      setErrorMessage(null);
    } catch (error) {
      console.error("Error sending data:", error);
      setLoading(false);
      setErrorMessage("Failed to submit form. Please try again later.");
      toast.error("Failed to submit form. Please try again later.");
    }
  };

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [id]: type === "checkbox" ? checked : value,
    });
  };

  // ---------------- Directory + Map (NEW) ----------------
  const offices = [
    {
      id: "sas",
      name: "SAS Partnership — Authorized Training Desk",
      category: "SAS",
      city: "Bengaluru",
      state: "Karnataka",
      address:
        "305, Tropical Terrace, Basavanagar, Doddanekkundi, Bangalore North, Bangalore - 560037, Karnataka",
      phones: ["+91 99009 11649 / +91 96635 70334"],
      email: "info@unithink.in",
      mapQuery: "Basavanagara, Bengaluru, Karnataka 560037",
    },
    {
      id: "blr",
      name: "UniThink Bengaluru (HQ)",
      category: "HQ",
      city: "Bengaluru",
      state: "Karnataka",
      address:
        "305, Tropical Terrace, Basavanagar, Doddanekkundi, Bangalore North, Bangalore - 560037, Karnataka",
      phones: ["+91 99009 11649 / +91 96635 70334"],
      email: "info@unithink.in",
      mapQuery: "Basavanagara, Bengaluru, Karnataka 560037",
    },
    {
      id: "del",
      name: "UniThink Nepal",
      category: "Office",
      city: "Makawanpur",
      state: "Nepal",
      address: "Bhutan Devi Raod, Hetauda - 10, Makawanpur Nepal",
      phones: ["+91 99009 11649 / +91 96635 70334"],
      email: "info@unithink.in",
      mapQuery: "New Delhi, Delhi",
    },
    {
      id: "mum",
      name: "UniThink Jharkhand",
      category: "Office",
      city: "Bokaro",
      state: "Jharkhand",
      address: "Vidya classes #42, FVI, Jharnadih, Chandrapura Bokaro",
      phones: ["+91 99009 11649 / +91 96635 70334"],
      email: "info@unithink.in",
      mapQuery: "Mumbai, Maharashtra",
    },
    {
      id: "hyd",
      name: "UniThink Bihar",
      category: "Office",
      city: "Purnea",
      state: "Bihar",
      address: "Redchief Building, NH31,Opp-electricity board chowk Po&dist purnia-854301 Bihar.",
      phones: ["+91 99009 11649 / +91 96635 70334"],
      email: "info@unithink.in",
      mapQuery: "Hyderabad, Telangana",
    },
    {
      id: "pune",
      name: "UniThink NCR",
      category: "Office",
      city: "Delhi",
      state: "NCR",
      address: "Delhi NCR Jai Devi Vidya Mandir Public School, 427, 428, Wazirabad Rd, Bhopura, Sahibabad, Ghaziabad, Uttar Pradesh 201006",
      phones: ["+91 99009 11649 / +91 96635 70334"],
      email: "info@unithink.in",
      mapQuery: "Pune, Maharashtra",
    },
    {
      id: "dubai",
      name: "UniThink Dubai",
      category: "Office",
      city: "Dubai",
      state: "UAE",
      address: "DSO – IFZA Dubai Digital Park Dubai Silicone Oasis P.O.Box 98601 Dubai, UAE",
      phones: ["+91 99009 11649 / +91 96635 70334"],
      email: "info@unithink.in",
      mapQuery: "Dubai, UAE",
    },
  ];

  const [queryText, setQueryText] = useState("");
  const [stateFilter, setStateFilter] = useState("All");
  const [onlySAS, setOnlySAS] = useState(false);
  const [selectedId, setSelectedId] = useState(offices[0]?.id || "");
  const listRef = useRef(null);
  const mapContainerRef = useRef(null);

  const states = useMemo(() => {
    const vals = Array.from(new Set(offices.map((o) => o.state))).sort();
    return ["All", ...vals];
  }, [offices]);

  const filtered = useMemo(() => {
    return offices.filter((o) => {
      const t = queryText.trim().toLowerCase();
      const matchesText =
        !t ||
        [o.name, o.city, o.state, o.address].some((v) =>
          v.toLowerCase().includes(t)
        );
      const matchesState = stateFilter === "All" || o.state === stateFilter;
      const matchesSAS = !onlySAS || o.category === "SAS";
      return matchesText && matchesState && matchesSAS;
    });
  }, [offices, queryText, stateFilter, onlySAS]);

  const selected = useMemo(
    () => offices.find((o) => o.id === selectedId) || offices[0],
    [offices, selectedId]
  );

  const mapSrc = useMemo(() => {
    return "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.0572214403587!2d77.6788839!3d12.968190300000002!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1305d644aca9%3A0xc8564dbeb4a6a226!2sUnithink%20Education%20International%20Pvt.%20Ltd.!5e0!3m2!1sen!2sin!4v1756835184699!5m2!1sen!2sin";
  }, []);


  const scrollList = (dir) => {
    const el = listRef.current;
    if (!el) return;
    const delta = Math.max(320, Math.round(el.clientWidth * 0.9));
    el.scrollBy({ left: dir === "left" ? -delta : delta, behavior: "smooth" });
  };

  const focusMap = (id) => {
    if (id) setSelectedId(id);
    setTimeout(() => {
      mapContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  return (
    <div className="w-full text-gray-900">
      <ToastContainer />

      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700">
        <div className="relative mx-auto max-w-6xl px-6 py-14 md:py-18 text-white">
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight">Contact UniThink</h1>
          <p className="mt-3 max-w-2xl text-base md:text-lg leading-relaxed text-white/90">
            Have a question or need guidance? Reach us via phone, email, or the form below. As an
            <span className="mx-1 font-semibold">SAS Authorized Partner</span>, we also run a dedicated SAS training desk.
          </p>

          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {[{
              title: "Call Us",
              text: "+91 99009 11649 / +91 96635 70334",
              href: "tel:+919663570334",
              Icon: HiOutlinePhone,
            }, {
              title: "Email",
              text: "info@unithink.in",
              href: "mailto:info@unithink.in",
              Icon: HiMail,
            }, {
              title: "Address (HQ)",
              text: "305, Tropical Terrace, Basavanagar, Bengaluru Urban, 560037, Karnataka",
              href: "#map",
              Icon: HiOutlineLocationMarker,
            }].map((c) => (
              <div key={c.title} className="rounded-2xl border-white/20 bg-white/10 text-white backdrop-blur shadow-xl p-5 flex items-start gap-4">
                <c.Icon className="h-6 w-6" />
                <div>
                  <div className="text-lg font-semibold">{c.title}</div>
                  <a href={c.href} className="block text-white/90 hover:underline">{c.text}</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main content: Left = your original form (unchanged), Right = SAS desk + map + directory */}
      <section className="mx-auto max-w-6xl px-6 py-12 grid lg:grid-cols-2 gap-8">
        {/* ---------------- Your ORIGINAL Booking Form (no edits) ---------------- */}
        <div className="flex flex-col md:w-full p-0">
          <div className="mt-2">
            <a href="https://wa.me/message/GIWSQYCHN67RD1 " target="_blank" rel="noopener noreferrer"></a>
          </div>

          <div id="contact-form" className="mx-auto text-center">
            <div className="sttt">
              <h1 className="font-bold text-3xl md:text-4xl lg:text-4xl text-black relative flex flex-col items-center">
                <span className="mt-2">REQUEST FREE CONSULTATION</span>
                <span className="relative mt-2">
                  <span className="h-1 bg-blue-800 w-20 block"></span>
                </span>
              </h1>
            </div>
          </div>

          <div className="md:p-5 lg:p-8 mx-3 sm:mx-auto lg:mx-auto max-w-md md:max-w-lg lg:max-w-3xl mb-10" style={{ zIndex: 1 }}>
            <div className="discover">
              <p className="text-base md:text-lg text-center md:text-left">
                Book a session now! Don't worry, we won't charge you for anything...
              </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <Label value="Name*" />
                <TextInput type="text" placeholder="Enter your name" id="name" value={formData.name} onChange={handleChange} className="rounded-md shadow-sm border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50" />
              </div>
              <div>
                <Label value="Email*" />
                <TextInput type="email" placeholder="Enter your email" id="email" value={formData.email} onChange={handleChange} rightIcon={HiMail} className="rounded-md shadow-sm border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50" />
              </div>
              <div>
                <Label value="Phone*" />
                <TextInput type="number" placeholder="Enter your number" id="phonenumber" value={formData.phonenumber} onChange={handleChange} className="rounded-md shadow-sm border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50" />
              </div>
              <div>
                <Label value="Country*" />
                <TextInput type="text" placeholder="Enter your country" id="country" value={formData.country} onChange={handleChange} className="rounded-md shadow-sm border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50" />
              </div>
              <div>
                <Label value="Service Type*" />
                <Select id="serviceType" value={formData.serviceType} onChange={handleChange} required className="rounded-md shadow-sm border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
                  <option value="">Select Service Type</option>
                  <option value="Education & Career Counselling">Education & Career Counselling</option>
                  <option value="Find Your University & Course ">Find Your University & Course </option>
                  <option value="Skilling Academy ">Skilling Academy </option>
                  <option value="Education & Business Counsultancy">Education & Business Counsultancy</option>
                </Select>
              </div>
              {/* Additional Checkboxes */}
              <label htmlFor="agreeTerms" className="text-sm">
                UniThink will not share your details with others without your permission:
              </label>
              <div className="flex items-center">
                <Checkbox id="agreeTerms" checked={formData.agreeTerms} onChange={handleChange} className="mr-2" />
                <label htmlFor="agreeTerms" className="text-sm">
                  I agree to Unithink Terms <a href="/terms" className="text-blue-500">UniThink Terms</a> and <a href="/privacypolicy" className="text-blue-500">privacy policy</a>
                </label>
              </div>
              <div className="flex items-center">
                <Checkbox id="contactPreference" checked={formData.contactPreference} onChange={handleChange} className="mr-2" />
                <label htmlFor="contactPreference" className="text-sm">Please contact me by phone, email or SMS to assist with my enquiry</label>
              </div>
              <div className="flex items-center">
                <Checkbox id="receiveUpdates" checked={formData.receiveUpdates} onChange={handleChange} className="mr-2" />
                <label htmlFor="receiveUpdates" className="text-sm">I would like to receive updates and offers from UniThink</label>
              </div>
              {/* End of Additional Checkboxes */}
              <FlowbiteButton className="bg-gradient-to-r from-blue-700 to-blue-800 text-white" type="submit" disabled={loading}>
                {loading ? "Booking..." : "Book Now!"}
              </FlowbiteButton>
            </form>
            {errorMessage && <p className="text-red-500">{errorMessage}</p>}
            {successMessage && <p className="text-green-500">{successMessage}</p>}
          </div>
        </div>
        {/* ---------------- END of original form ---------------- */}

        {/* Right column: SAS Desk, Map, Offices Directory */}
        <div className="space-y-6">
          <div id="sas-desk" className="rounded-2xl border border-gray-100 p-5 bg-white shadow-sm">
            <div className="font-semibold">SAS Authorized Partnership</div>
            <div className="text-gray-600 text-sm mt-1">Official training & certifications desk. For SAS-specific queries, use the form and select “SAS Training / Certification.”</div>
          </div>

          <div ref={mapContainerRef} id="map" className="rounded-2xl overflow-hidden shadow border border-gray-100">
            <iframe title="Map" src={mapSrc} className="w-full h-[320px] md:h-[380px]" loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
          </div>

          <div className="rounded-2xl border border-gray-100 p-5 bg-white shadow-sm">
            <div className="flex flex-wrap items-center gap-3">
              <input
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
                placeholder="Search city, state or office name..."
                className="p-3 border rounded-lg w-full sm:w-auto flex-1 min-w-[220px] focus:outline-none focus:ring-2 focus:ring-red-500/50"
              />
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/50"
              >
                {states.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>

            </div>

            <div className="relative mt-4">
              <div ref={listRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2">
                {filtered.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => focusMap(o.id)}
                    className={`snap-start text-left shrink-0 min-w-[260px] md:min-w-[320px] rounded-2xl border p-4 transition hover:-translate-y-0.5 ${selectedId === o.id ? "border-blue-700 shadow" : "border-gray-200 shadow-sm"
                      }`}
                  >
                    <div className="flex items-center gap-2 font-semibold">
                      <span className="inline-block rounded bg-blue-50 text-blue-700 px-2 py-0.5 text-xs">{o.category}</span>
                      {o.name}
                    </div>
                    <div className="mt-1 text-sm text-gray-600 flex items-center gap-2"><HiOutlineLocationMarker className="h-4 w-4" /> {o.city}, {o.state}</div>
                    <div className="mt-2 text-sm text-gray-700 line-clamp-2">{o.address}</div>
                    <div className="mt-2 text-sm text-gray-700 flex items-center gap-2"><HiOutlinePhone className="h-4 w-4" /> {o.phones.join(" / ")}</div>
                    <div className="mt-1 text-sm text-gray-700 flex items-center gap-2"><HiMail className="h-4 w-4" /> {o.email}</div>
                  </button>
                ))}
              </div>
              <div className="hidden sm:block">
                <button onClick={() => scrollList("left")} className="absolute -left-3 top-1/2 -translate-y-1/2 rounded-full bg-white shadow p-2 hover:bg-gray-50" aria-label="Prev">
                  <HiChevronLeft className="h-5 w-5" />
                </button>
                <button onClick={() => scrollList("right")} className="absolute -right-3 top-1/2 -translate-y-1/2 rounded-full bg-white shadow p-2 hover:bg-gray-50" aria-label="Next">
                  <HiChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Offices Grid (10+ HQ & Cities) */}
      <section className="mx-auto max-w-6xl px-6 pt-2 pb-12">
        <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">Our Offices</h3>
        <p className="text-gray-600 mb-6">We’re available across key cities. Tap a card to view it on the map or call directly.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {offices.map((o) => (
            <div key={o.id} className={`rounded-2xl border ${selectedId === o.id ? 'border-blue-700' : 'border-gray-200'} bg-white hover:shadow-md transition`}>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="font-semibold leading-tight">{o.name}</div>
                  {o.category === 'SAS' && (
                    <span className="text-[11px] rounded-full bg-blue-50 text-blue-700 px-2 py-0.5">SAS</span>
                  )}
                </div>
                <div className="mt-2 text-sm text-gray-700 flex items-center gap-2"><HiOutlineLocationMarker className="h-4 w-4" /> {o.city}, {o.state}</div>
                <div className="mt-2 text-sm text-gray-700">{o.address}</div>
                <div className="mt-2 text-sm text-gray-700 flex items-center gap-2"><HiOutlinePhone className="h-4 w-4" /> {o.phones.join(' / ')}</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {/* <button onClick={() => focusMap(o.id)} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white h-9 px-4">View on Map</button> */}
                  <a href="tel:+919963521332">
                    <button className="rounded-full h-9 px-4 bg-white text-gray-900 border border-gray-300 hover:bg-gray-50">Call</button>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQs */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-4">Frequently Asked</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <details className="rounded-xl border p-4">
            <summary className="font-semibold cursor-pointer">How soon will I hear back after submitting the form?</summary>
            <p className="mt-2 text-gray-700">Typically within 1 business day. For urgent matters, call our Bengaluru desk.</p>
          </details>
          <details className="rounded-xl border p-4">
            <summary className="font-semibold cursor-pointer">Do you offer SAS exam preparation?</summary>
            <p className="mt-2 text-gray-700">Yes. As an official SAS Authorized Partner, we provide training paths, labs, and certification prep.</p>
          </details>
          <details className="rounded-xl border p-4">
            <summary className="font-semibold cursor-pointer">Can I book in-person counselling?</summary>
            <p className="mt-2 text-gray-700">Yes, at select offices. Mention your city in the form and our team will schedule a slot.</p>
          </details>
          <details className="rounded-xl border p-4">
            <summary className="font-semibold cursor-pointer">Where can I see all services?</summary>
            <p className="mt-2 text-gray-700">Visit the Services page or ask for a tailored counselling plan in your message.</p>
          </details>
        </div>
      </section>

      {/* Footer CTAs */}
      <section className="w-full bg-gray-50">
        <div className="mx-auto max-w-6xl px-6 py-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faWhatsapp} className="h-6 w-6 text-green-600" />
            <div>
              <div className="font-semibold">Prefer WhatsApp?</div>
              <div className="text-gray-600 text-sm">We’ll reply during business hours.</div>
            </div>
          </div>
          <div className="flex gap-3">
            <a href="https://wa.me/919663521332" target="_blank" rel="noreferrer">
              <button className="rounded-full bg-green-600 hover:bg-green-700 text-white px-4 py-2">Chat on WhatsApp</button>
            </a>
            <a href="#top">
              <button className="rounded-full border px-4 py-2">Back to top</button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
